/**
 * [BaselineConfigs] used for Step 2 and Step 4 of the Baseline module, handling configuration and access controls.
 * - Renders per-period, list, and access controls for the current baseline.
 * - Uses local state for editing, syncing with parent (BaselineMain) via props and callbacks.
 * - Loads config types, users, and groups via useBaselineConfigsData custom hook.
 *
 * - All data-fetching and lookup logic is handled in the custom hook.
 * - Only UI and simple validation/state changes here.
 * - Deep clone before mutating baseline/config state to avoid mutating Redux.
 * - All updates are bubbled up via onLocalBaselineChange and handled by BaselineMain for saving.
 * - Uses verifyFields() where applicable to standardize validation.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item - The current baseline being edited.
 * @param {string} props.mode - Controls editability of the form.
 * @param {string} [props.size] - UI size for text fields/selects.
 * @param {number} [props.formFieldWidth] - Width for select fields.
 * @param {boolean} props.dataChanged - Whether config data is modified.
 * @param {number} props.groupId - Used to filter configs/lookups.
 * @param {function(Object):void} props.onLocalBaselineChange - Callback for local config changes.
 * @param {function(boolean):void} props.onClearComplete - Callback to reset parent temp UI state.
 * @param {function(number, function):void} props.onSaveDataToDB - Callback to trigger DB save.
 * @returns {React.ReactElement}
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useUpdateEffect } from "react-use";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";

// Layout/UI
import { Box, Stack } from "@metrea-ui/v2";
import BaselineConfigPeriod from "src/components/UI/Input/TextfieldWithPeriod";
import BaselineLookupList from "src/components/UI/ConfigsByChips/ConfigsByChips";
import FormMultiSelect from "src/components/UI/common/FormMultiSelect";

// Utilities
import {
	deepClone,
	mergeArrayById,
	getValuesByPropertyPath,
	mergeArrayByPropertyWithDeletes,
} from "src/utils/dataUtils";
import { Session } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { updateAccessArray } from "src/utils/accessControlUtils";

const REQUIRED_BASELINE_CONFIG_CODES = new Set(["military_service", "domain", "category"]);

const BaselineConfigs = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;
	const uiSize = props.size;
	const groupId = props.groupId;
	const onValidateData = props.onValidateData;

	// Local copy of the current baseline (driven from props)
	const initialBaselineSafe = props.item && typeof props.item === "object" ? props.item : {};
	const [currBaseline, setCurrBaseline] = useState(initialBaselineSafe);

	// Used to track if a new config item is being added.
	const [newItemAdded, setNewItemAdded] = useState(props.dataChanged);
	const [periodValidation, setPeriodValidation] = useState({});

	// Custom hooks
	const { allUsers, allDesigners, allUserGroups, sortedCfgTypes, updateSortedCfgTypes } = useLookupsData();

	// --- Effect: sync local when parent item changes
	useEffect(() => {
		debugLog(2, "[BaselineConfigs][FLOW] mounted", {});
	}, []);

	useUpdateEffect(() => {
		// defensive null checks
		if (props.item && typeof props.item === "object") {
			setCurrBaseline(props.item);
		} else {
			setCurrBaseline({});
		}
		debugLog(3, "[BaselineConfigs][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	useEffect(() => {
		// Clear only on explicit reset or when switching baseline/group context.
		setPeriodValidation({});
	}, [props.resetKey, props.groupId, props.item?.id]);

	const hasMissingPeriodValues = useMemo(() => {
		if (!currBaseline || !Array.isArray(sortedCfgTypes)) return false;

		const periodTypes = sortedCfgTypes.filter((cfg) => cfg?.type_id === 1);
		return periodTypes.some((cfg) => {
			const rows = currBaseline[`cfg_${cfg.code}`];
			if (!Array.isArray(rows)) return false;
			return rows.some((row) => `${row?.comp_value ?? ""}`.trim() === "");
		});
	}, [currBaseline, sortedCfgTypes]);

	const missingRequiredListCodes = useMemo(() => {
		if (!currBaseline || !Array.isArray(sortedCfgTypes)) return [];

		// Keep the UI-required state aligned with the existing Step 2 save gate
		// instead of inferring from every lookup config in the group.
		return sortedCfgTypes
			.filter((cfg) => cfg?.type_id === 2 && REQUIRED_BASELINE_CONFIG_CODES.has(cfg?.code))
			.filter((cfg) => {
				const rows = currBaseline[`cfg_${cfg.code}`];
				return !Array.isArray(rows) || rows.length === 0;
			})
			.map((cfg) => cfg.code);
	}, [currBaseline, sortedCfgTypes]);

	useEffect(() => {
		if (typeof onValidateData === "function") {
			// Baseline Step 2 is invalid when either a per-period field is incomplete
			// or one of the required lookup groups is still empty on first load/edit.
			const hasPeriodError = Object.values(periodValidation).some(Boolean) || hasMissingPeriodValues;
			onValidateData(hasPeriodError || missingRequiredListCodes.length > 0);
		}
	}, [periodValidation, hasMissingPeriodValues, missingRequiredListCodes, onValidateData]);

	// --- Effect: Update sortedCfgTypes whenever groupId changes
	useEffect(() => {
		updateSortedCfgTypes(groupId);
	}, [groupId, updateSortedCfgTypes]);

	/**
	 * Handles updates for config period items (e.g., per-period fields).
	 * Deep clones current baseline, merges changed configs, notifies parent.
	 * @param {Array} item - The array of config items to update.
	 * @param {string} cfgCategory - The category key for the configs.
	 * @returns {void}
	 */
	const updateConfigPeriodHandler = (item, cfgCategory) => {
		debugLog(3, "[BaselineConfigs][ACTION] updateConfigPeriodHandler", {
			cfgCategory: cfgCategory,
			itemCount: item?.length ?? 0,
		});
		/**
		 * LEGACY REFERENCE (pre-refactor):
		 *
		 * This handler previously:
		 * - Deep-cloned the baseline
		 * - Replaced ONLY configs with `update_flag === 102`
		 * - Left untouched configs unchanged
		 *
		 * That logic is now centralized in:
		 *   dataUtils.mergeArrayById(...)
		 *
		 * Keeping this reference to document *intent*:
		 * - Period configs should ONLY update when the user actually changed a value
		 * - Prevents churn of untouched baseline_configs
		 */

		/**
		 * Use mergeArrayById to:
		 * - Only update baseline_configs whose `id` matches an item with `update_flag === 102`
		 * - This means: only configs that the user actually edited (period values changed) will be updated in baseline_configs.
		 * - All other configs remain unchanged.
		 *
		 * Example:
		 * - If user edits Period 3's value for "Domain", only that config's value will be updated in the array.
		 * - Prevents unnecessary churn of configs that were not modified.
		 */
		const cloneBaseline = mergeArrayById(
			currBaseline,
			"baseline_configs",
			item, // The updated configs for this period/category
			"id",
			(x) => x.update_flag === 102, // Only configs flagged for update
		);

		debugLog(4, "[BaselineConfigs][PROCESS] updateConfigPeriodHandler.merge", {
			item: item,
			cfgCategory: cfgCategory,
			cloneBaseline: cloneBaseline,
		});

		// Level 9 (optional firehose)
		debugLog(9, "[BaselineConfigs][PROCESS] updateConfigPeriodHandler.firehose", {
			currBaseline: currBaseline,
			cloneBaseline: cloneBaseline,
			item: item,
			cfgCategory: cfgCategory,
		});

		const updateData = {
			...cloneBaseline,
			[cfgCategory]: item,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		// Update local state.
		setCurrBaseline(updateData);

		// Notify parent component of changes.
		props.onLocalBaselineChange(updateData);

		// Save data immediately whenever data is changed on Step 2 per Todd's request 9/14/2023
		// disabled this, and save data to the db when switch tabs instead - 4/9/2025
		// props.onSaveDataToDB(-1);

		// Reset any temp UI flags in parent if needed.
		handleClearComplete();
	};

	/**
	 * Handles updates for list-type config fields (chips/tags).
	 * Merges and deletes by comp_type_id, syncs with parent and local state.
	 * @param {Array} item - The list of configs selected.
	 * @param {string} cfgCategory - The category key for the configs.
	 * @param {number} comp_type_id - The config type ID (scope for deletes).
	 * @param {boolean} [updateDbNow=false] - If true, triggers DB save.
	 * @returns {void}
	 */
	const updateConfigListHandler = (item, cfgCategory, comp_type_id, updateDbNow = false) => {
		debugLog(3, "[BaselineConfigs][ACTION] updateConfigListHandler", {
			cfgCategory: cfgCategory,
			comp_type_id: comp_type_id,
			itemCount: item?.length ?? 0,
		});
		/**
		 * LEGACY REFERENCE (pre-refactor):
		 *
		 * List configs (chips/tags) previously required:
		 * - Manual add of new selections
		 * - Manual removal of deselected items
		 * - Careful scoping by comp_type_id
		 *
		 * This behavior is now centralized in:
		 *   dataUtils.mergeArrayByPropertyWithDeletes(...)
		 *
		 * Rationale:
		 * - List configs are authoritative per comp_type_id
		 * - Deselection must REMOVE stale rows
		 * - Selection must ADD or REPLACE rows
		 */

		/**
		 * Use mergeArrayByPropertyWithDeletes to:
		 * - ADD or REPLACE config items in baseline_configs that match by `id`
		 * - DELETE any baseline_configs for the current `comp_type_id` that are no longer selected
		 *   (i.e., chips/tags the user removed)
		 *
		 * Example scenario:
		 * - User selects new "Military Service" chips. Any previously existing chips for Military Service
		 *   that are NOT in the new selection will be removed. All new/changed chips will be added or updated.
		 * - This prevents stale configs from remaining if a user deselects a tag/chip.
		 */
		const cloneBaseline = mergeArrayByPropertyWithDeletes(
			currBaseline,
			"baseline_configs",
			item, // the user's new chip/tag selection (array)
			"id", // unique key for merge
			null, // no additional filter (could pass a function if needed)
			"comp_type_id", // scope deletes to this property
			comp_type_id, // only remove configs for this specific type (e.g., Military Service)
		);

		debugLog(4, "[BaselineConfigs][PROCESS] updateConfigListHandler.merge", {
			cloneBaseline: cloneBaseline,
			item: item,
			cfgCategory: cfgCategory,
			comp_type_id: comp_type_id,
			updateDbNow: updateDbNow,
		});

		// Level 9 (optional)
		debugLog(9, "[BaselineConfigs][PROCESS] updateConfigListHandler.firehose", {
			currBaseline: currBaseline,
			cloneBaseline: cloneBaseline,
			item: item,
			cfgCategory: cfgCategory,
			comp_type_id: comp_type_id,
		});

		const updateData = {
			...cloneBaseline,
			[cfgCategory]: item,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		// Update local state.
		setCurrBaseline(updateData);

		// Notify parent component of changes.
		props.onLocalBaselineChange(updateData);

		// Reset any temp UI flags in parent if needed.
		handleClearComplete();
	};

	/**
	 * Tracks the addition of a new config chip or autocomplete item.
	 * Used for temporary UI state only.
	 * @param {any} option - The new item being added.
	 * @returns {void}
	 */
	const handleAddNewItem = (option) => {
		debugLog(3, "[BaselineConfigs][ACTION] handleAddNewItem", {
			option: option,
			newItemAdded: newItemAdded,
		});
		setNewItemAdded(option);
	};

	/**
	 * Handles updates for access control configs (users/groups).
	 * Updates baseline access arrays and notifies parent.
	 * @param {Array} selected - The selected access configs.
	 * @param {Object} cfgCategory - The config category object.
	 * @returns {void}
	 */
	const updateAccessHandler = (selected, cfgCategory) => {
		debugLog(3, "[BaselineConfigs][ACTION] updateAccessHandler", {
			cfgCategory: cfgCategory,
			selectedCount: selected?.length ?? 0,
		});
		/**
		 * LEGACY REFERENCE (pre-refactor):
		 *
		 * Access control updates previously:
		 * - Marked removed access rows with update_flag === 103
		 * - Reused existing rows if re-selected
		 * - Created new rows with update_flag === 101
		 * - Preserved baseline_id / scenario_id ownership
		 *
		 * This state machine now lives in:
		 *   accessControlUtils.updateAccessArray(...)
		 *
		 * The utility guarantees:
		 * - Correct add / remove semantics
		 * - Stable update_flag transitions
		 * - No duplicate access rows
		 */

		const arrayTemp = updateAccessArray(
			currBaseline.baseline_accesses,
			selected,
			cfgCategory.id,
			currBaseline.id, // Or scenario.id for Scenario module
			Session().currUser.id,
			"baseline_id",
		);
		const cloneBaseline = { ...deepClone(currBaseline), baseline_accesses: arrayTemp };

		debugLog(4, "[BaselineConfigs][PROCESS] updateAccessHandler.merge", {
			selected: selected,
			cfgCategory: cfgCategory,
			arrayTemp: arrayTemp,
			cloneBaseline: cloneBaseline,
		});

		const updateData = {
			...cloneBaseline,
			baseline_accesses: [...arrayTemp],
			[`cfg_${cfgCategory.code}`]: arrayTemp.filter((x) => {
				return x.comp_type_id === cfgCategory.id && x.update_flag !== 103;
			}),
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		setCurrBaseline(updateData);
		props.onLocalBaselineChange(updateData);

		handleClearComplete();
	};

	/**
	 * Calls parent clear/reset handler.
	 * @returns {void}
	 */
	const handleClearComplete = () => {
		debugLog(3, "[BaselineConfigs][ACTION] handleClearComplete", {});
		props.onClearComplete();
	};

	const handlePeriodValidation = (cfgCode, validation) => {
		const payload =
			validation && typeof validation === "object"
				? validation
				: { hasError: !!validation, cfgCode: cfgCode, reason: null };
		const hasError = !!payload.hasError;
		const prevHasError = periodValidation[cfgCode];
		if (prevHasError === hasError) return;
		// High-volume signal: keep at a higher debug level and log only when the value changes.
		debugLog(7, "[BaselineConfigs][ACTION] handlePeriodValidation", {
			cfgCode: cfgCode,
			from: prevHasError,
			to: hasError,
			reason: payload.reason,
			fieldLabel: payload?.lastValidationMeta?.label ?? payload?.firstMissingField?.label ?? null,
			fieldIndex: payload?.lastValidationMeta?.index ?? payload?.firstMissingField?.index ?? null,
			fieldId: payload?.lastValidationMeta?.id ?? payload?.firstMissingField?.id ?? null,
			fieldValue: payload?.lastValidationMeta?.value ?? null,
		});
		setPeriodValidation((prev) => {
			if (prev[cfgCode] === hasError) return prev;
			return { ...prev, [cfgCode]: hasError };
		});
	};

	// Debug print for dev inspection.
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineConfigs][FLOW] render", {
		renderCount: renderCount.current,
		currBaseline: currBaseline,
	});
	debugLog(9, "[BaselineConfigs][FLOW] renderState", {
		props: props,
		currBaseline: currBaseline,
		newItemAdded: newItemAdded,
		sortedCfgTypes: sortedCfgTypes,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			<Box w="100%">
				{/* Direct v2 Stack uses gap; keep the previous wrapper-era spacing here. */}
				<Stack gap={2}>
					{currBaseline &&
						Array.isArray(sortedCfgTypes) &&
						sortedCfgTypes.length > 0 &&
						sortedCfgTypes.map((cfg, index) => {
							if (cfg.type_id === 1) {
								// Render config period inputs.
								return (
									<div key={cfg.id}>
										<BaselineConfigPeriod
											mode={pageMode}
											size={uiSize || "small"}
											type={cfg}
											periodLabel={currBaseline.cfg_period_label}
											item={currBaseline[`cfg_${cfg.code}`] || []}
											currencySymbol={currBaseline.currency_symbol}
											currencyUnit={currBaseline.currency_unit}
											currencyDisplayDecimal={currBaseline.currency_display_decimal}
											onUpdate={updateConfigPeriodHandler}
											onClearComplete={handleClearComplete}
											onValidateData={(hasError) => handlePeriodValidation(cfg.code, hasError)}
											resetKey={props.resetKey}
											requiredSectionLabel
										/>
									</div>
								);
							} else if (cfg.type_id === 2) {
								// Render config list chips (lookups).
								const isRequiredConfig = REQUIRED_BASELINE_CONFIG_CODES.has(cfg.code);
								const isMissingRequiredConfig = isRequiredConfig && missingRequiredListCodes.includes(cfg.code);
								return (
									<div key={cfg.id}>
										<BaselineLookupList
											mode={pageMode}
											type={cfg}
											baselineId={currBaseline.id}
											item={currBaseline[`cfg_${cfg.code}`] || []}
											onUpdate={updateConfigListHandler}
											onClearComplete={handleClearComplete}
											onAddNewItem={handleAddNewItem}
											dataChanged={props.dataChanged}
											required={isRequiredConfig}
											error={isMissingRequiredConfig}
											helperText={
												isMissingRequiredConfig ? `Please select at least one ${cfg.name} value.` : ""
											}
										/>
									</div>
								);
							} else if (cfg.type_id === 3) {
								// Render config access controls (users/groups).
								const allGroupItems =
									Array.isArray(allUserGroups)
										? allUserGroups
										: [];
								// Keep PUBLIC as read-policy only; never expose it as an editable ACL target.
								const filteredGroupItems =
									cfg.code === "edit_access_group"
										? allGroupItems.filter(
												(group) =>
													Number(group?.id) !== -100 &&
													String(group?.group_code || "").toUpperCase() !== "PUBLIC",
											)
										: allGroupItems;
								const arrayItems =
									cfg.flag1 === 1
										? filteredGroupItems
										: cfg.code === "edit_access_user"
											? allDesigners
											: allUsers;
								// Derive ids from the legacy access rows: [{ comp_value }]
								const selectedAccessIds =
									getValuesByPropertyPath(currBaseline[`cfg_${cfg.code}`], "comp_value") ||
									[];

								return (
									<div key={cfg.id}>
										<FormMultiSelect
											mode={pageMode}
											type={cfg}
											items={arrayItems}
											selected={selectedAccessIds}
											onUpdate={updateAccessHandler}
										/>
									</div>
								);
							}
							return false;
						})}
				</Stack>
			</Box>
		</>
	);
};

export default BaselineConfigs;
